import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip, Collapse, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import React, { useState } from 'react';
import { translate } from '../../../../../common/intl';
import { Checkbox, FormControlLabel } from '../../../../components/forms/fields-next';
import FBAutocomplete from '../../../../form.builder/FBAutocomplete/FBAutocomplete';
import { ApprovalConfigurationStage, APPROVER_TYPE, EmployeeOption, GroupOption, REQUIRED_APPROVERS, StageApprover } from '../../types';
import ApproverRow from './ApproverRow';
import useStyles from './dialog.styles';
import { StageValidationError } from './helpers';
import ValidationPanel from './ValidationPanel';

export interface Props {
  stage: ApprovalConfigurationStage
  isActionable: boolean
  onAddApprover: (stageNumber: number, approverId: string) => void
  onRemoveApprover: (stageNumber: number, index: number) => void
  onAddGroup: (stageNumber: number, groupId: string) => void
  onRequiredCountChange: (stageNumber: number, count: REQUIRED_APPROVERS) => void
  onOptionalApproverRejectionChange: (stageNumber: number, value: boolean) => void
  onHandleApproverDetailsUpdated: (stageNumber: number, approverId: string, updates: Partial<StageApprover>) => void
  onStageNameChange: (stageNumber: number, value: string) => void
  onDelete: (stageNumber: number) => void
  allApprovers: EmployeeOption[]
  allGroups: GroupOption[]
  validationErrors: StageValidationError[]
}

const ApprovalStageComp: React.FC<Props> = ({
  stage, isActionable, onAddApprover, onRemoveApprover, onAddGroup, onRequiredCountChange, onHandleApproverDetailsUpdated,
  onDelete, allApprovers, allGroups, validationErrors, onOptionalApproverRejectionChange, onStageNameChange,
}) => {
  const classes = useStyles();

  const [selectorType, setSelectorType] = useState<APPROVER_TYPE>(
    APPROVER_TYPE.USER,
  );

  const [isAddApproverOpen, setIsAddApproverOpen] = useState(false);
  const handleAddApproverClick = () => {
    setIsAddApproverOpen(prev => !prev);
  };

  const [isEditing, setIsEditing] = React.useState(false);
  const [stageName, setStageName] = React.useState(
    stage.name || `Stage ${stage.stageNumber}`,
  );
  const [editedName, setEditedName] = React.useState(stageName);

  const handleStartEditing = () => {
    setEditedName(stageName);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedName = editedName.trim();
    if (trimmedName) {
      setStageName(trimmedName);
      onStageNameChange(stage.stageNumber, trimmedName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setEditedName(stageName);
      setIsEditing(false);
    }
  };

  const hasApprovers = stage.assignedApprovers?.length > 0;
  const hasOptionalApprover = stage.assignedApprovers?.some(approver => approver.isOptional);

  const assignedUserApproverIds = stage.assignedApprovers
    .filter(approver => approver.type === APPROVER_TYPE.USER)
    .map(approver => approver.id);

  const assignedGroupApproverIds = stage.assignedApprovers
    .filter(approver => approver.type === APPROVER_TYPE.GROUP)
    .map(approver => approver.id);

  const availableApprovers = allApprovers.filter(
    approver => !assignedUserApproverIds.includes(approver.id),
  );

  const availableGroups = allGroups.filter(
    group => !assignedGroupApproverIds.includes(group.id),
  );

  const selectedGroups = allGroups.filter(
    group => assignedGroupApproverIds.includes(group.id),
  );

  const renderUser = (option: EmployeeOption) => (
    <Box>
      <Box className={classes.userHeader}>
        <Typography variant="body1" className={classes.optionName}>
          {option.name}
        </Typography>

        <Box className={classes.chipOptions}>
          {option.satisfyingGroups.map(group => (
            <Chip
              key={group}
              size="small"
              label={group}
              className={classes.chip}
            />
          ))}
        </Box>
      </Box>

      <Typography variant="caption" noWrap className={classes.optionDetails}>
        {option.email}
      </Typography>
    </Box>
  );

  const renderGroup = (option: GroupOption) => (
    <Box>
      <Box className={classes.userHeader}>
        <Typography variant="body1" className={classes.optionName}>
          {option.name}
        </Typography>

        <Box className={classes.chipOptions}>
          <Chip
            size="small"
            label={`${option.joinedEmployees.length} Employees`}
            className={classes.chip}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className={classes.stageWrapper}>
      <ValidationPanel errors={validationErrors} />
      <Box className={classes.stageBox}>

        <Box className={classes.stageTitle}>
          {isEditing ? (
            <TextField
              value={editedName}
              onChange={(event) => setEditedName(event.target.value.slice(0, 30))}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              inputProps={{ maxLength: 30 }}
              variant="outlined"
              className={classes.stageNameField}
              inputRef={(input) => {
                if (input) {
                  input.focus();
                }
              }}
            />
          ) : (
            <Typography
              variant="h4"
              onClick={handleStartEditing}
              className={classes.editableStageName}
            >
              {stageName}
              <FontAwesomeIcon icon={solid('pencil')} className={classes.editNameIcon} />
            </Typography>
          )}
          {isActionable && <Box className={classes.deleteIconBox}
            onClick={() => onDelete(stage.stageNumber)}>
            <FontAwesomeIcon icon={solid('trash-can')} /> Delete
          </Box> }
        </Box>

        { hasApprovers && <Grid container className={classes.stageHeader}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Minimum Approvals</InputLabel>
              <Select
                value={stage.requiredCount}
                onChange={(e) => onRequiredCountChange(stage.stageNumber, e.target.value as REQUIRED_APPROVERS)}
              >
                {Object.values(REQUIRED_APPROVERS).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          { hasOptionalApprover ? <Grid item xs={12} className={classes.stageHeaderRight}>
            <FormControl fullWidth className={classes.genericFlex}>
              <FormControlLabel
                className={classes.optionalApproverSelect}
                control={
                  <Checkbox
                    checked={stage.optionalApproverRejectionRejectsStage}
                    disabled={!isActionable}
                    onChange={(event) =>
                      onOptionalApproverRejectionChange(
                        stage.stageNumber,
                        event.target.checked,
                      )
                    }
                    color="primary"
                  />
                }
                label={translate(
                  'approval.configuration.dialog.stage.optional.approver.rejection.label',
                )}
              />
              <Tooltip
                title={translate(
                  'approval.configuration.dialog.stage.optional.approver.rejection.hint',
                )}
                placement="right"
                classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }}
                arrow>
                <InfoIcon color="primary" />
              </Tooltip>
            </FormControl>
          </Grid> : <></> }
        </Grid> }

        <Box className={classes.approverList}>
          {stage.assignedApprovers.map((approver, index) => (
            <ApproverRow
              key={index}
              approver={approver}
              index={index}
              onIsOptionalChange={(approverId: string, isOptional: boolean) =>
                onHandleApproverDetailsUpdated(stage.stageNumber, approverId, { isOptional })}
              onDelete={(index: number) => onRemoveApprover(stage.stageNumber, index)}
              isActionable={isActionable}
              availableApprovers={availableApprovers}
              selectedGroups={selectedGroups}
            />
          ))}
        </Box>

        <Collapse in={isAddApproverOpen}>
          <Box className={classes.addApproverContainer}>
            <ToggleButtonGroup
              value={selectorType}
              exclusive
              onChange={(_, value) => value && setSelectorType(value)}
              size="small"
              className={classes.toggleButtonGroups}
            >
              <ToggleButton
                value={APPROVER_TYPE.USER}
                className={classes.toggleButton}
              > Add User
              </ToggleButton>

              <ToggleButton
                value={APPROVER_TYPE.GROUP}
                className={classes.toggleButton}
              > Add From Group
              </ToggleButton>
            </ToggleButtonGroup>

            <Box mt={2}>
              {selectorType === APPROVER_TYPE.USER
                && <FBAutocomplete
                  placeholder="approver.configure.approverName.autocomplete.placeholder"
                  options={availableApprovers}
                  optionLabelKey="name"
                  optionValueKey="id"
                  gutter={false}
                  omitFormValue={false}
                  name="approvers-name"
                  onChange={(e, value) => {
                    onAddApprover(stage.stageNumber, value?.id);
                  }}
                  disabled={!isActionable}
                  renderOption={renderUser}
                  groupByKey="N/A"
                />}
              {selectorType === APPROVER_TYPE.GROUP
                && <FBAutocomplete
                  placeholder="approver.configure.groupName.autocomplete.placeholder"
                  options={availableGroups}
                  optionLabelKey="name"
                  optionValueKey="id"
                  gutter={false}
                  omitFormValue={false}
                  name="approvers-group"
                  onChange={(e, value) =>
                    onAddGroup(stage.stageNumber, value?.id)}
                  renderOption={renderGroup}
                  disabled={!isActionable}
                />}
            </Box>
          </Box>
        </Collapse>

        { isActionable && <Box className={classes.stageFooter}>
          <Button
            startIcon={<FontAwesomeIcon icon={solid('add')} />}
            variant="outlined"
            onClick={handleAddApproverClick}
            className={classes.addApproverBtn}>
            {isAddApproverOpen ? 'Close' : 'Add Approver'}
          </Button>
        </Box> }

      </Box>
    </Box>
  );
};

export default ApprovalStageComp;
